import React from "react"

interface MessageData {
	messageId: string
	timeoutId: NodeJS.Timeout
	content?: string
}

interface UndoContextState {
	pendingMessages: string[]
	setPendingMessage: (
		messageId: string,
		pendingTime?: number,
		content?: string,
	) => void
	removePendingMessage: (messageId: string) => void
	isMessageIncluded: (messageId: string) => boolean
	getMessageContent: (messageId: string) => string | undefined
}

const UndoContext = React.createContext<UndoContextState>({
	pendingMessages: [],
	setPendingMessage: (_: string) => undefined,
	removePendingMessage: (_: string) => undefined,
	isMessageIncluded: (_: string) => false,
	getMessageContent: (_: string) => undefined,
})

export const MESSAGE_UNDO_ANIMATION_DURATION = 500
export const MESSAGE_UNDO_CANCELLATION_TIMEOUT = 10000

export const UndoContextProvider = ({ children }: React.PropsWithChildren) => {
	const messages = React.useRef<MessageData[]>([])
	const [pendingMessages, setPendingMessages] = React.useState<string[]>([])

	const removePendingMessage = (messageId: string) => {
		const index = messages.current.findIndex(
			(data) => data.messageId === messageId,
		)

		if (index > -1) {
			clearTimeout(messages.current[index]?.timeoutId)
			messages.current = [
				...messages.current.slice(0, index),
				...messages.current.slice(index + 1),
			]
			setPendingMessages(messages.current.map(({ messageId }) => messageId))
		}
	}

	const setPendingMessage = (
		messageId: string,
		time?: number,
		content?: string,
	) => {
		const timeoutId = setTimeout(
			() => {
				removePendingMessage(messageId)
			},
			// Only time over 10 second is considered valid
			// we anyway use only 10 seconds delay to show the undo sent message, related to this issue
			// https://linear.app/productlane/issue/PRO-4428/undo-sent-disappears-way-too-fast
			time && time > 10000
				? time
				: MESSAGE_UNDO_CANCELLATION_TIMEOUT - MESSAGE_UNDO_ANIMATION_DURATION,
		) // 10s - 500ms because of animation duration

		if (!isMessageIncluded(messageId)) {
			messages.current = [
				...messages.current,
				{ messageId, timeoutId, content },
			]
			setPendingMessages(messages.current.map(({ messageId }) => messageId))
		}
	}

	const isMessageIncluded = (messageId: string) =>
		pendingMessages.includes(messageId)

	const getMessageContent = (messageId: string) => {
		const message = messages.current.find(
			(data) => data.messageId === messageId,
		)
		return message?.content
	}

	const value = React.useMemo(
		() => ({
			pendingMessages,
			setPendingMessage,
			removePendingMessage,
			isMessageIncluded,
			getMessageContent,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pendingMessages.length],
	)

	return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>
}

export const useUndoContext = () => {
	const context = React.useContext(UndoContext)
	if (!context) {
		throw Error("Wrap your modal with `<UndoContextProvider />`")
	}
	return context
}
